import type { APIResponse } from "./types";

/**
 * API client error with status code and server message.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly serverMessage: string,
  ) {
    super(`API error ${status}: ${serverMessage}`);
    this.name = "ApiError";
  }
}

/** Function that returns the current access token. */
export type TokenProvider = () => string | null;

/** Function that attempts to refresh the token. Returns true on success. */
export type TokenRefresher = () => Promise<boolean>;

/** Function that redirects to login. */
export type LoginRedirect = () => void;

/**
 * Base HTTP client for the openDesk operator API.
 *
 * Injects JWT Bearer tokens from the auth layer.
 * Handles 401 responses by attempting one token refresh before redirecting to login.
 */
export class BaseClient {
  private readonly baseUrl: string;
  private readonly getToken: TokenProvider;
  private readonly refreshToken: TokenRefresher;
  private readonly redirectToLogin: LoginRedirect;

  constructor(
    getToken: TokenProvider,
    refreshToken: TokenRefresher,
    redirectToLogin: LoginRedirect,
    baseUrl: string = "",
  ) {
    this.getToken = getToken;
    this.refreshToken = refreshToken;
    this.redirectToLogin = redirectToLogin;
    this.baseUrl = baseUrl;
  }

  /**
   * Perform a fetch request with auth headers and automatic 401 retry.
   */
  async request<T>(
    method: string,
    path: string,
    body?: unknown,
    headers?: Record<string, string>,
  ): Promise<APIResponse<T>> {
    const doRequest = async (): Promise<Response> => {
      const token = this.getToken();
      const reqHeaders: Record<string, string> = {
        "Content-Type": "application/json",
        ...headers,
      };

      if (token) {
        reqHeaders["Authorization"] = `Bearer ${token}`;
      }

      return fetch(`${this.baseUrl}${path}`, {
        method,
        headers: reqHeaders,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    };

    let response = await doRequest();

    // On 401, try refreshing the token once.
    if (response.status === 401) {
      const refreshed = await this.refreshToken();
      if (refreshed) {
        response = await doRequest();
      } else {
        this.redirectToLogin();
        throw new ApiError(401, "Session expired");
      }
    }

    // 204 No Content (e.g., DELETE).
    if (response.status === 204) {
      return {} as APIResponse<T>;
    }

    const json = (await response.json()) as APIResponse<T>;

    if (json.error) {
      throw new ApiError(json.error.code, json.error.message);
    }

    if (!response.ok) {
      throw new ApiError(response.status, "Unknown error");
    }

    return json;
  }

  /** HTTP GET. */
  async get<T>(path: string): Promise<APIResponse<T>> {
    return this.request<T>("GET", path);
  }

  /** HTTP POST. */
  async post<T>(path: string, body?: unknown): Promise<APIResponse<T>> {
    return this.request<T>("POST", path, body);
  }

  /** HTTP PUT. */
  async put<T>(path: string, body: unknown): Promise<APIResponse<T>> {
    return this.request<T>("PUT", path, body);
  }

  /** HTTP PATCH. */
  async patch<T>(path: string, body: unknown): Promise<APIResponse<T>> {
    return this.request<T>("PATCH", path, body);
  }

  /** HTTP DELETE (with optional confirmation header). */
  async delete(
    path: string,
    confirm: boolean = false,
    extraHeaders: Record<string, string> = {},
  ): Promise<APIResponse<void>> {
    const headers: Record<string, string> = { ...extraHeaders };
    if (confirm) {
      headers["X-Confirm-Delete"] = "true";
    }
    return this.request<void>("DELETE", path, undefined, headers);
  }
}
